// SMS-mottakere for et site. Speiler bitmesh.entity.service/Sites/AlarmContact.cs
// og bitmesh.entity.service/Entities/ServiceHoursDefinition.cs.
//
// NB: TS-interfacet i firemesh/chefmesh (edit-entity-form-react/interfaces.ts) sier
// feilaktig at from/to er strings. Backend forventer TimeDefinition-objekter, og
// days er (int)DayOfWeek - altsa sondag = 0.

export interface TimeDefinition {
  hour: number;
  minute: number;
}

export interface ServiceHoursDefinition {
  days: number[];
  from: TimeDefinition;
  to: TimeDefinition;
}

export interface AlarmContact {
  name: string;
  phoneNumber: string;
  isActive: boolean;
  sendSmsHours?: ServiceHoursDefinition[] | null;
  // Meldingstypene mottakeren skal få SMS for. Mangler eller tom betyr standardvalget.
  messageCategories?: string[] | null;
}

// Meldingstyper en mottaker kan velge. Verdiene er MessageCategory i bitmesh.firemesh.service,
// og listen holdes lik bitmesh.firemesh.app/src/app/site-settings-react/messageCategories.ts.
export const MESSAGE_CATEGORIES = [
  { value: 'Alarm', label: 'Alarmer', hint: 'Brannalarm, B-alarm og feil på sløyfe' },
  { value: 'AlarmDisabledTooLong', label: 'Alarm deaktivert for lenge', hint: 'Alarmen har vært slått av i over én time' },
  { value: 'Test', label: 'Test', hint: 'Test feilet, batch-test og alarm aktiv etter test' },
  { value: 'Watchdog', label: 'Oppetid / kommunikasjon', hint: 'Mistet kontakt med ventilen' },
];

// Det en mottaker uten valg får; må stemme med BaseAlarmListenerJob.ReceivesCategory.
export const DEFAULT_MESSAGE_CATEGORIES = MESSAGE_CATEGORIES.map((category) => category.value).filter(
  (value) => value !== 'Watchdog'
);

export const contactCategories = (contact: AlarmContact): string[] =>
  contact.messageCategories?.length ? contact.messageCategories : DEFAULT_MESSAGE_CATEGORIES;

// "Alarmer, Test" i valgt rekkefølge fra listen over
export function formatMessageCategories(contact: AlarmContact): string {
  const selected = contactCategories(contact);
  return MESSAGE_CATEGORIES.filter((category) => selected.includes(category.value))
    .map((category) => category.label)
    .join(', ');
}

// value = (int)DayOfWeek slik backend forventer. Rekkefolgen her er visningsrekkefolgen
// (mandag forst), ikke verdirekkefolgen.
export const WEEKDAYS: { value: number; short: string }[] = [
  { value: 1, short: 'Man' },
  { value: 2, short: 'Tir' },
  { value: 3, short: 'Ons' },
  { value: 4, short: 'Tor' },
  { value: 5, short: 'Fre' },
  { value: 6, short: 'Lør' },
  { value: 0, short: 'Søn' },
];

const pad = (value: number) => String(value).padStart(2, '0');

export function formatTime(time: TimeDefinition | null | undefined): string {
  if (!time) return '--:--';
  return `${pad(time.hour ?? 0)}:${pad(time.minute ?? 0)}`;
}

// "08:00" -> { hour: 8, minute: 0 }
export function parseTimeInput(value: string): TimeDefinition {
  const [hour, minute] = (value || '').split(':');
  return { hour: Number(hour) || 0, minute: Number(minute) || 0 };
}

export function toTimeInput(time: TimeDefinition | null | undefined): string {
  if (!time) return '';
  return `${pad(time.hour ?? 0)}:${pad(time.minute ?? 0)}`;
}

// Slår sammen sammenhengende ukedager til "Man-Fre". Dagene sorteres i visningsrekkefolge
// (mandag forst), ikke etter tallverdi, slik at sondag havner sist.
export function formatDays(days: number[] | null | undefined): string {
  if (!days || days.length === 0) return 'Ingen dager';

  const ordered = WEEKDAYS.filter((day) => days.includes(day.value));
  if (ordered.length === 0) return 'Ingen dager';
  if (ordered.length === WEEKDAYS.length) return 'Alle dager';

  const groups: string[] = [];
  let runStart = 0;
  for (let i = 0; i < ordered.length; i++) {
    const isLastInRun =
      i === ordered.length - 1 ||
      WEEKDAYS.indexOf(ordered[i + 1]) !== WEEKDAYS.indexOf(ordered[i]) + 1;
    if (isLastInRun) {
      const runLength = i - runStart + 1;
      groups.push(
        runLength >= 3
          ? `${ordered[runStart].short}-${ordered[i].short}`
          : ordered.slice(runStart, i + 1).map((day) => day.short).join(', ')
      );
      runStart = i + 1;
    }
  }
  return groups.join(', ');
}

export function formatServiceHours(hours: ServiceHoursDefinition): string {
  return `${formatDays(hours.days)} ${formatTime(hours.from)}-${formatTime(hours.to)}`;
}

// Tom eller manglende liste betyr "send alltid" - se ServiceHourUtils.ServiceHoursOverlapWith.
export function formatSendSmsHours(hours: ServiceHoursDefinition[] | null | undefined): string {
  if (!hours || hours.length === 0) return 'Alltid';
  return hours.map(formatServiceHours).join('; ');
}
